import { Button, ButtonWithLoader } from '@neo/primitives/button'
import { Checkbox } from '@neo/primitives/checkbox'
import { Input } from '@neo/primitives/input'
import { ChevronLeft, ChevronRight, Search, X } from 'lucide-react'
import { useState } from 'react'
import type { RAgentJudge, RAoSession } from 'v2/types/api/agent-observability'
import dayjs from 'v2/utils/dayjs'
import {
  SESSIONS_PAGE_SIZE,
  useRecentSessionsQuery,
  useTestMetricMutation,
} from './metrics-tab.queries'
import { TestResults } from './metrics-test-results'
import { RunLink } from './metrics-test-shared'

const MAX_TEST_SESSIONS = 10

interface MetricsTestPanelProps {
  judge: RAgentJudge
  agentId: string
  onClose: () => void
  onTested?: (judgeId: string, summary: { calls: number; failed: number }) => void
  onCalibrated?: (judgeId: string, description: string) => void
}

export const MetricsTestPanel = ({
  judge,
  agentId,
  onClose,
  onTested,
  onCalibrated,
}: MetricsTestPanelProps) => {
  const [selected, setSelected] = useState<string[]>([])
  const [step, setStep] = useState<'pick' | 'results'>('pick')
  const [offset, setOffset] = useState(0)
  const [search, setSearch] = useState('')
  const sessionsQuery = useRecentSessionsQuery(agentId, true, offset)
  const testMutation = useTestMetricMutation()

  const sessions = sessionsQuery.data?.objects ?? []
  const total = sessionsQuery.data?.meta?.total_count ?? sessions.length
  const visible = sessions.filter((s: RAoSession) =>
    search ? s.session_id.toLowerCase().includes(search.toLowerCase()) : true,
  )

  const toggleSession = (sessionId: string) =>
    setSelected((prev) => {
      if (prev.includes(sessionId)) return prev.filter((id) => id !== sessionId)
      return prev.length >= MAX_TEST_SESSIONS ? prev : [...prev, sessionId]
    })

  const runTest = () =>
    testMutation.mutate(
      { judgeId: judge.id, sessionIds: selected },
      {
        onSuccess: (res) => {
          setStep('results')
          onTested?.(judge.id, { calls: res.summary.scored, failed: res.summary.failed })
        },
      },
    )

  return (
    <div
      className="flex flex-col max-h-[calc(100vh-12rem)] overflow-hidden rounded-xl border border-st2 bg-background"
      data-testid="metrics-test-panel"
    >
      <div className="flex items-start justify-between gap-3 border-b border-st2 px-4 py-3">
        <span className="flex min-w-0 flex-col gap-0.5">
          <span className="text-xxs-600 text-tertiary uppercase tracking-wide">Test metric</span>
          <span className="text-p-500 text-primary truncate">{judge.display_name}</span>
        </span>
        <button
          type="button"
          onClick={onClose}
          className="text-tertiary hover:text-primary shrink-0"
          aria-label="Close test panel"
          data-testid="btn-test-panel-close"
        >
          <X size={16} />
        </button>
      </div>

      <div className="flex-1 overflow-y-auto px-4 py-3">
        {step === 'results' && testMutation.data ? (
          <TestResults
            judge={judge}
            flowId={agentId}
            data={testMutation.data}
            onBack={() => setStep('pick')}
            onCalibrated={(id, desc) => onCalibrated?.(id, desc)}
            onDone={onClose}
          />
        ) : (
          <div className="flex flex-col gap-3">
            <Input
              startIcon={Search}
              placeholder="Search by call ID"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              data-testid="input-test-search"
            />

            <div className="flex items-center justify-between text-s-400">
              <span className="text-secondary">
                {selected.length} of {MAX_TEST_SESSIONS} selected
              </span>
              <span className="flex items-center gap-3">
                <button
                  type="button"
                  className="text-primary"
                  onClick={() =>
                    setSelected(visible.slice(0, MAX_TEST_SESSIONS).map((s) => s.session_id))
                  }
                  data-testid="btn-select-first"
                >
                  Select first {MAX_TEST_SESSIONS}
                </button>
                <button
                  type="button"
                  className="text-tertiary"
                  onClick={() => setSelected([])}
                  data-testid="btn-clear-selection"
                >
                  Clear
                </button>
              </span>
            </div>

            {sessionsQuery.isPending ? (
              <div className="flex flex-col gap-2 py-3" data-testid="test-sessions-loading">
                {['a', 'b', 'c', 'd', 'e'].map((k) => (
                  <div key={k} className="h-8 rounded-md bg-muted animate-pulse" />
                ))}
              </div>
            ) : (
              <div className="flex flex-col" data-testid="list-test-sessions">
                <div className="flex items-center justify-between px-1 pb-2 text-xxs-600 text-tertiary uppercase tracking-wide">
                  <span>Call ID</span>
                  <span>Started</span>
                </div>
                {visible.length === 0 && (
                  <p className="text-s-400 text-secondary py-3">
                    No calls match — run a call first, then test.
                  </p>
                )}
                {visible.map((session) => (
                  <div
                    key={session.session_id}
                    className="flex items-center justify-between gap-3 border-t border-st2 py-2.5"
                    data-testid={`row-test-session-${session.session_id}`}
                  >
                    <span className="flex items-center gap-2">
                      <Checkbox
                        id={`test-session-${session.session_id}`}
                        name={`test-session-${session.session_id}`}
                        value={selected.includes(session.session_id)}
                        onChange={() => toggleSession(session.session_id)}
                        label={<span className="sr-only">Select call</span>}
                      />
                      <RunLink
                        flowId={agentId}
                        sessionId={session.session_id}
                        runId={session.flow_run_uuid}
                      />
                    </span>
                    <span className="text-s-400 text-secondary">
                      {dayjs(session.started_at).format('MMM D, HH:mm')}
                    </span>
                  </div>
                ))}
                <div className="flex items-center justify-between border-t border-st2 pt-3 text-s-400">
                  <span className="text-tertiary">
                    Showing {sessions.length} of {total} calls
                  </span>
                  <span className="flex items-center gap-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      disabled={offset === 0}
                      onClick={() => setOffset((o) => Math.max(0, o - SESSIONS_PAGE_SIZE))}
                      data-testid="btn-sessions-prev"
                    >
                      <ChevronLeft size={14} /> Previous
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      disabled={offset + SESSIONS_PAGE_SIZE >= total}
                      onClick={() => setOffset((o) => o + SESSIONS_PAGE_SIZE)}
                      data-testid="btn-sessions-next"
                    >
                      Next <ChevronRight size={14} />
                    </Button>
                  </span>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {step === 'pick' && (
        <div className="flex items-center justify-end border-t border-st2 px-4 py-3">
          <ButtonWithLoader
            loading={testMutation.isPending}
            disabled={selected.length === 0}
            onClick={runTest}
            data-testid="btn-run-test"
          >
            Run on {selected.length} {selected.length === 1 ? 'call' : 'calls'}
          </ButtonWithLoader>
        </div>
      )}
    </div>
  )
}
